import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Spinner, Badge, Alert } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { formatDistanceToNow } from 'date-fns';

interface Reserve {
  asset?: string;
  amount?: string;
}

interface Pool {
  id?: string;
  paging_token?: string;
  fee_bp?: number;
  type?: string;
  total_trustlines?: string;
  total_shares?: string;
  reserves?: Reserve[];
  last_modified_ledger?: number;
  last_modified_time?: string;
  _links?: {
    self?: {
      href?: string;
    };
    operations?: {
      href?: string;
    };
    transactions?: {
      href?: string;
    };
  };
}

interface PoolsApiResponse {
  _embedded: {
    records: Pool[];
  };
  _links: {
    self: {
      href: string;
    };
    next?: {
      href: string;
    };
    prev?: {
      href: string;
    };
  };
}

const Pools: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://api.testnet.minepi.com/liquidity_pools?limit=200&order=desc');
      
      if (!response.ok) {
        throw new Error('Failed to fetch liquidity pools');
      }
      
      const data: PoolsApiResponse = await response.json();
      setPools(data._embedded?.records || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPools();
  }, [fetchPools]);

  const formatAsset = (assetString?: string): string => {
    if (!assetString) return 'Unknown';
    if (assetString === 'native') {
      return 'PI';
    }
    
    const parts = assetString.split(':');
    if (parts.length === 2) {
      const [assetCode, issuer] = parts;
      return `${assetCode}:${issuer.substring(0, 8)}...`;
    }
    
    return assetString;
  };

  const formatAmount = (amount?: string): string => {
    if (!amount || amount === '') return '0';
    const num = parseFloat(amount);
    if (isNaN(num)) return '0';
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 7 
    });
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString();
  };

  const formatReserveAsset = (reserve: Reserve): React.ReactElement => {
    const assetDisplay = formatAsset(reserve.asset);
    const amount = formatAmount(reserve.amount);
    
    return (
      <div key={reserve.asset || 'unknown'} className="mb-1">
        <strong>{amount}</strong> {assetDisplay}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="my-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <div className="alert alert-danger">{error}</div>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="mb-1">{t('pools.heading')}</h2>
          <Alert variant="warning" className="mb-3">
            <strong>{t('pools.testnet_warning')}</strong> {t('pools.testnet_description')}
          </Alert>
        </Col>
      </Row>

      {/* Pools Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t('pools.liquidity_pools_list')}</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>{t('pools.pool_id')}</th>
                      <th>{t('pools.reserves')}</th>
                      <th>{t('pools.total_shares')}</th>
                      <th className="text-center">{t('pools.fee')}</th>
                      <th>{t('pools.trustlines')}</th>
                      <th>{t('pools.last_modified')}</th>
                      <th className="text-center">{t('pools.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pools.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          <div>
                            <h6 className="text-muted">{t('pools.no_pools')}</h6>
                            <small className="text-muted">
                              {t('pools.no_pools_desc')}
                            </small>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pools.map((pool) => (
                        <tr key={pool.id || `pool-${Math.random()}`}>
                          <td>
                            <code className="small text-break">
                              {pool.id ? `${pool.id.substring(0, 8)}...${pool.id.substring(pool.id.length - 8)}` : 'Unknown'}
                            </code>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              {pool.reserves?.map((reserve, index) => formatReserveAsset(reserve)) || <span className="text-muted">No reserves</span>}
                            </div>
                          </td>
                          <td>
                            <strong>{formatAmount(pool.total_shares)}</strong>
                          </td>
                          <td className="text-center">
                            <Badge bg="info">
                              {((pool.fee_bp || 0) / 100).toFixed(2)}%
                            </Badge>
                          </td>
                          <td>
                            <span className="text-muted">
                              {parseInt(pool.total_trustlines || '0').toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <small>{formatDateTime(pool.last_modified_time)}</small>
                              <small className="text-muted">
                                {pool.last_modified_time ? formatDistanceToNow(new Date(pool.last_modified_time), { addSuffix: true }) : 'Unknown'}
                              </small>
                            </div>
                          </td>
                          <td className="text-center">
                            {pool.id ? (
                              <a 
                                href={`/pool/${pool.id}/history`}
                                className="text-primary text-decoration-none small"
                                title={t('pools.view_operations')}
                              >
                                {t('pools.operations')}
                              </a>
                            ) : (
                              <span className="text-muted small">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Pools;
